import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { MONTH_NAMES } from '../../audit/budgets';
import { easternIsoDay } from '../../data/taskRules';
import { QUARTER_NAMES, startView, type MonthView } from './budgetImport';

/** Where twelve month columns fit (owner ruling 2026-10-27: quarters, "All 12" on wide screens). */
const NARROW = '(max-width: 1099px)';

const isNarrow = () => {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(NARROW).matches;
  } catch {
    return false;
  }
};

/**
 * Which months a budget grid shows: a quarter, or "All 12" on wide screens.
 * `wideStart` = start on All 12 where it fits (the store page's read-only
 * overview); otherwise the year's current quarter (the Budgets page). A
 * window narrowed (or a phone turned) goes back to a quarter.
 */
export function useMonthView(year: number, wideStart = false): [MonthView, Dispatch<SetStateAction<MonthView>>] {
  const [view, setView] = useState<MonthView>(() => (wideStart && !isNarrow() ? 'all' : startView(year, easternIsoDay(new Date()))));
  useEffect(() => {
    if (view !== 'all' || typeof window.matchMedia !== 'function') return;
    const narrow = window.matchMedia(NARROW);
    const back = () => narrow.matches && setView(startView(year, easternIsoDay(new Date())));
    back();
    narrow.addEventListener('change', back);
    return () => narrow.removeEventListener('change', back);
  }, [view, year]);
  return [view, setView];
}

/** Q1–Q4 (+ "All 12", hidden where it doesn't fit). */
export function MonthViewSwitch({ view, onChange }: { view: MonthView; onChange: (v: MonthView) => void }) {
  return (
    <div className="view-switch budget-quarters" role="group" aria-label="Months shown">
      {QUARTER_NAMES.map((q, i) => (
        <button key={q} type="button" aria-pressed={view === i} onClick={() => onChange(i as 0 | 1 | 2 | 3)}>
          {q} <span className="muted">({MONTH_NAMES[i * 3]}–{MONTH_NAMES[i * 3 + 2]})</span>
        </button>
      ))}
      <button type="button" className="budget-all12" aria-pressed={view === 'all'} onClick={() => onChange('all')}>
        All 12
      </button>
    </div>
  );
}
